import React, { useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, Trash2, Loader2 } from "lucide-react";

const CONFIRM_PHRASE = "WIPE_ALL_TEST_DATA";

export default function DangerZonePage() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [dryReport, setDryReport] = useState(null);
  const [confirmInput, setConfirmInput] = useState("");

  const runDryRun = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/wipe-test-data", { dry_run: true });
      setDryReport(r.data);
      toast.info(`Trovati ${r.data?.total || 0} record da cancellare`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore dry-run");
    } finally {
      setBusy(false);
    }
  };

  const runWipe = async () => {
    if (busy) return;
    if (confirmInput !== CONFIRM_PHRASE) {
      toast.error(`Devi scrivere esattamente: ${CONFIRM_PHRASE}`);
      return;
    }
    const ok = window.confirm(
      `⚠️ ULTIMA CONFERMA\n\nStai per cancellare DEFINITIVAMENTE:\n• Tutti i clienti\n• Tutte le polizze\n• Tutti gli Anträge\n• Tutti i dati collegati (commissioni, dossier, task, appuntamenti, …)\n\nQuesta azione è IRREVERSIBILE. Procedere?`
    );
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/wipe-test-data", {
        dry_run: false,
        confirm: CONFIRM_PHRASE,
      });
      toast.success(`${r.data?.total || 0} record cancellati`);
      setDryReport(r.data);
      setConfirmInput("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore wipe");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="danger-zone-page">
      <div>
        <button
          onClick={() => navigate(-1)}
          className="label-uppercase text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Zurück
        </button>
        <div className="label-uppercase text-red-600">/ Danger Zone</div>
        <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight mt-1">
          Reset Dati di Test
        </h1>
      </div>

      <div className="prime-card p-6 border-red-300 bg-red-50/30 space-y-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-1" />
          <div className="space-y-2 text-sm">
            <p className="font-semibold text-red-900">
              Questa operazione cancella TUTTI i clienti, polizze, Anträge e dati collegati
              per ogni utente del sistema.
            </p>
            <p className="text-red-800">
              Vengono preservati: utenti, compagnie, schemi commissionali, configurazioni,
              template email.
            </p>
            <p className="text-red-800">
              Operazione <strong>irreversibile</strong>. Esegui prima un dry-run per vedere i conteggi.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            onClick={runDryRun}
            disabled={busy}
            data-testid="danger-dryrun-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Dry-run (anteprima conteggi)
          </Button>
        </div>

        {dryReport && (
          <div className="border border-red-200 rounded-md p-4 bg-white space-y-3">
            <div className="font-semibold text-sm">
              {dryReport.dry_run ? "Anteprima" : "Eseguito"} — totale:{" "}
              <span className="text-red-700 font-bold">{dryReport.total}</span> record
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs font-mono">
              {Object.entries(dryReport.full || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-stone-100 py-1">
                  <span className="text-stone-600">{k}</span>
                  <span className={v > 0 ? "text-red-700 font-bold" : "text-stone-400"}>{v}</span>
                </div>
              ))}
              {Object.entries(dryReport.conditional || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-stone-100 py-1">
                  <span className="text-stone-600">{k} (filtrato)</span>
                  <span className={v > 0 ? "text-red-700 font-bold" : "text-stone-400"}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-red-200 pt-4 space-y-3">
          <label className="block">
            <span className="label-uppercase text-red-700">
              Per confermare, scrivi: <code className="font-mono">{CONFIRM_PHRASE}</code>
            </span>
            <Input
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              className="mt-2 font-mono"
              data-testid="danger-confirm-input"
            />
          </label>
          <Button
            variant="destructive"
            onClick={runWipe}
            disabled={busy || confirmInput !== CONFIRM_PHRASE}
            className="bg-red-600 hover:bg-red-700"
            data-testid="danger-wipe-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Cancella TUTTO definitivamente
          </Button>
        </div>

        {/* Targeted cleanup: Doc Designer + WertiaBox + Dienste (Service-Formulare).
            These three features were retired and we provide a one-shot wipe so the
            admin can reclaim the data in production after the next redeploy. */}
        <div className="border-t-2 border-amber-300 pt-4 mt-6 space-y-3">
          <div>
            <div className="label-uppercase text-amber-700">Aufgegebene Module entfernen</div>
            <p className="text-sm text-stone-600 mt-1">
              Löscht ausschliesslich die Daten der zurückgezogenen Module:
              <b> Doc Designer</b> (Vorlagen + Instanzen + QR-Signaturen),
              <b> WertiaBox</b> (gesendete und empfangene E-Mails + Threads) und
              <b> Dienste</b> (Tandem, Mandato, Beratungsprotokoll, Angemessenheit, Maklervollmacht, Kündigung).
              Kunden / Anträge / Polizen bleiben unangetastet.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                if (busy) return;
                setBusy(true);
                try {
                  const r = await api.post("/admin/wipe-docdesigner-wertiabox", { dry_run: true });
                  toast.info(`${r.data?.total || 0} Datensätze betroffen`);
                  setDryReport(r.data);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Fehler beim Dry-Run");
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
              data-testid="danger-dd-dry-run-btn"
            >
              Dry-Run (zählen)
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (busy) return;
                const ok = window.confirm(
                  "⚠️ Daten von Doc Designer + WertiaBox + Dienste werden DEFINITIV gelöscht.\nFortfahren?"
                );
                if (!ok) return;
                setBusy(true);
                try {
                  const r = await api.post("/admin/wipe-docdesigner-wertiabox", { dry_run: false });
                  toast.success(`${r.data?.total || 0} Datensätze gelöscht`);
                  setDryReport(r.data);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Fehler beim Löschen");
                } finally {
                  setBusy(false);
                }
              }}
              disabled={busy}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="danger-dd-wipe-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Doc Designer + WertiaBox + Dienste leeren
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
